import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const InfoBannerAdmin = () => {
  const [enabled, setEnabled] = useState(false);
  const [textCs, setTextCs] = useState("");
  const [textEn, setTextEn] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("app_settings")
        .select("key,value")
        .in("key", ["info_banner_enabled", "info_banner_text_cs", "info_banner_text_en", "info_banner_text"]);
      const map = new Map((data ?? []).map((r: any) => [r.key, r.value]));
      setEnabled(map.get("info_banner_enabled") === "true");
      // Backfill from legacy single-text key if present
      setTextCs(map.get("info_banner_text_cs") ?? map.get("info_banner_text") ?? "");
      setTextEn(map.get("info_banner_text_en") ?? "");
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase
      .from("app_settings")
      .upsert(
        [
          { key: "info_banner_enabled", value: enabled ? "true" : "false" },
          { key: "info_banner_text_cs", value: textCs },
          { key: "info_banner_text_en", value: textEn },
        ],
        { onConflict: "key" },
      );
    setSaving(false);
    if (error) return toast.error(error.message);
    toast.success("Info panel uložen");
  };

  if (loading) return <p className="text-muted-foreground text-sm">Načítám…</p>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Info panel</CardTitle>
        <CardDescription>
          Zpráva zobrazená nad kartami lig na hlavní stránce. Vyplňte obě jazykové verze.
          Pro nový řádek stiskněte Enter. Tučný text obalte dvěma hvězdičkami, např. <code>**důležité**</code>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-3">
          <div>
            <Label htmlFor="info-banner-enabled" className="cursor-pointer">Zobrazit info panel</Label>
            <p className="text-xs text-muted-foreground">Když je vypnuto, panel se uživatelům nezobrazí.</p>
          </div>
          <Switch id="info-banner-enabled" checked={enabled} onCheckedChange={setEnabled} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="info-banner-text-cs">Text 🇨🇿 (čeština)</Label>
          <Textarea
            id="info-banner-text-cs"
            value={textCs}
            onChange={(e) => setTextCs(e.target.value)}
            placeholder="Např. Doplněno rozlosování o další zápasy…"
            rows={4}
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground">{textCs.length}/500</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="info-banner-text-en">Text 🇬🇧 (English)</Label>
          <Textarea
            id="info-banner-text-en"
            value={textEn}
            onChange={(e) => setTextEn(e.target.value)}
            placeholder="E.g. Schedule updated with more matches…"
            rows={4}
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground">{textEn.length}/500</p>
        </div>
        <Button onClick={save} disabled={saving}>
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Uložit
        </Button>
      </CardContent>
    </Card>
  );
};
